/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useCosmosComponentContext } from "@components/context";
import {
	CosmosCard,
	CosmosImage,
	CosmosLink,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
} from "@components/elements";
import { getFeedCardHref } from "@components/elements/molecules/feed-cards/feed-card.utils";
import type { AnalyticsProps } from "@components/types";
import { formatTimeAndDate } from "@components/utils";
import { NewsCardFragment } from "@cosmos/hygraph-schema";
import { Avatar } from "@eds-web/react/Avatar";
import { Tag } from "@eds-web/react/Tag";
import { useLocale } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { useMemo } from "react";
import { newsCard } from "./NewsCard.style";

// override EDS styles
const badge = css({
	backgroundColor: "token(eds.fill.secondary.elveated.default) !important",
	"&:hover": {
		backgroundColor: "token(eds.fill.secondary.elevated.hover) !important",
	},
});

export type NewsCardProps = NewsCardFragment &
	AnalyticsProps & { tag: { tagId?: string }; originUrl?: string; date?: string | null; hideCategoryPills?: boolean };

export function NewsCard({
	image,
	href,
	title,
	description,
	tag,
	authorTag,
	analytics,
	originUrl,
	date,
	hideCategoryPills,
}: NewsCardProps): React.JSX.Element {
	const styles = newsCard();
	const locale = useLocale();
	const { feedRootPath = "/feed", locales, feedBrand } = useCosmosComponentContext();

	let tagHref: string;
	if (tag?.tagId) {
		tagHref = `/${locale}${feedRootPath}?tags=${tag?.tagId}`;
	} else {
		tagHref = tag?.href || "";
	}

	const formattedDate = useMemo(() => {
		if (!date) return "";
		try {
			return formatTimeAndDate(date, locale);
		} catch {
			console.warn("[NewsCard] Invalid date");
		}
		return "";
	}, [date, locale]);

	const showCategoryPill = Boolean(!hideCategoryPills && tag?.label && tagHref);

	return (
		<CosmosCard className={cx("news-card", styles.wrapper)} padding={12} variant="material" direction="row">
			{image?.url ? (
				<CosmosCard.Thumbnail className={cx("image-box", styles["image-box"])}>
					<CosmosImage
						className={cx("image", styles.image)}
						src={image?.url}
						width={263}
						height={148}
						loading="lazy"
						alt={title?.text || "News card"}
					/>
				</CosmosCard.Thumbnail>
			) : null}
			<VStack
				overflow="hidden"
				alignItems="flex-start"
				justifyContent="space-between"
				padding="eds.dimension.8"
				gap="eds.dimension.16"
				flex="1"
				minWidth="0"
			>
				<VStack alignItems="flex-start" gap="eds.dimension.12">
					{formattedDate ? (
						<CosmosText
							className={cx("news-card-eyebrow", css({ color: "eds.text.secondary" }))}
							text={formattedDate}
							variant={CosmosTextVariantEnum.eyebrowSM}
						/>
					) : null}
					<VStack alignItems="flex-start" gap="eds.dimension.8">
						{title?.html ? (
							<CosmosCard.Link
								href={getFeedCardHref(locale, locales, { url: href || "", originUrl }, feedBrand)}
								analytics={analytics}
							>
								<CosmosRichText
									variant={CosmosTextVariantEnum.paragraphMD}
									as="h3"
									html={title?.html}
									className={css({ fontWeight: "bold" })}
								/>
							</CosmosCard.Link>
						) : (
							""
						)}
						<CosmosRichText
							color="eds.text.secondary"
							variant={CosmosTextVariantEnum.paragraphSM}
							html={description?.html}
						/>
					</VStack>
				</VStack>
				{showCategoryPill || authorTag ? (
					<HStack alignItems={{ base: "start", sm: "center" }} gap="eds.dimension.16" fontSize="0">
						{showCategoryPill ? <Tag as={CosmosLink} className={badge} href={tagHref} label={tag?.label} /> : ""}
						{authorTag ? (
							<Avatar
								as={CosmosLink}
								href={authorTag?.href || ""}
								image={authorTag?.image?.url}
								label={authorTag?.label}
								size="sm"
							/>
						) : (
							""
						)}
					</HStack>
				) : (
					""
				)}
			</VStack>
		</CosmosCard>
	);
}
